import type { ReactNode, Ref } from 'react';
import {
  AccountCircleIcon,
  AddIcon,
  CloseIcon,
  ContentCopyIcon,
  DeleteIcon,
  DragIndicatorIcon,
  EditIcon,
  ExpandMoreIcon,
  HFBox,
  HFButton,
  HFCheckbox,
  HFChip,
  HFControlGroup,
  HFLogo,
  HFSkeleton,
  HFSwitch,
  HomeIcon,
  InvestorPortalIcon,
  RedoIcon,
  SaveIcon,
  SettingsIcon,
  SKINS,
  TEARSHEET_THEMES,
  TearsheetsIcon,
  UndoIcon,
  hf,
  hfAlpha,
  isDarkTearsheetSkin,
  type TearsheetTheme,
  type TearsheetThemeId,
} from '@hf/designSystem/v1';
import {
  CUSTOM_SKIN_ID,
  DEMO_ADDABLE,
  DEMO_BOARD,
  DEMO_CLIENT_LAYOUT,
  DEMO_COMMENTARY,
  DEMO_CREATE_AS_OF,
  DEMO_CREATE_FUND,
  DEMO_CUSTOM_LAYOUT_ID,
  DEMO_DRAWDOWNS,
  DEMO_FAVORITE_SLOTS,
  DEMO_FONTS,
  DEMO_FUNDS,
  DEMO_INDEXES,
  DEMO_KPIS,
  DEMO_MISSING_ROW_ID,
  DEMO_MONTHLY,
  DEMO_PM_BIO,
  DEMO_RANGE,
  DEMO_SKIN_ORDER,
  DEMO_STAT_OPTIONS,
  DEMO_STATS,
  DEMO_STATUS_FILTERS,
  DEMO_STOCK_LAYOUTS,
  DEMO_SUBTITLE,
  DEMO_VAMI,
  DEMO_WIDTHS,
  GRID_COLUMNS,
  gridRowCount,
  SKIN_TOKEN_LABELS,
  SKIN_TOKENS,
  STENCIL_LABELS,
  type DemoLayoutChoice,
  type DemoStatus,
  type GridItem,
  type StencilId,
} from './demoData';
import type { DemoState, DemoView } from './demoTour';

export const SHELL_WIDTH = 1200;
export const SHELL_HEIGHT = 720;

const TABS: Array<{ view: DemoView; label: string }> = [
  { view: 'reports', label: 'Reports' },
  { view: 'create', label: 'Create tearsheet' },
  { view: 'builder', label: 'Layout Builder' },
  { view: 'branding', label: 'Branding' },
];

const CHROME_BTN_SX = { height: 32, minHeight: 32, py: 0, whiteSpace: 'nowrap' } as const;
const ACTION_BTN_SX = { minWidth: 0, width: '100%', px: 0.4, py: 0, height: 28, minHeight: 28, fontSize: 12, whiteSpace: 'nowrap' } as const;
const CHROME_ICON_SX = { fontSize: 16 };
const FADE_COLORS = 'background-color 0.5s ease, color 0.5s ease, border-color 0.5s ease';
const SECTION_SX = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: 'text.primary',
  lineHeight: 1.2,
} as const;
const BRAND_GROUP_SX = {
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  alignItems: 'stretch',
  width: '100%',
  gap: 2,
  px: 2.5,
  pt: 3,
  pb: 2,
} as const;
const BOARD_COLUMNS = '108px minmax(0, 1fr) 120px minmax(0, 1fr) 340px';
const GRID_ROW_PX = 48;
const GRID_GAP_PX = 8;

const ALL_LAYOUTS: DemoLayoutChoice[] = [...DEMO_STOCK_LAYOUTS, DEMO_CLIENT_LAYOUT];

function layoutChoices(state: DemoState): Array<{ id: string; name: string; owner: string }> {
  const custom = state.customLayout ? [{ id: DEMO_CUSTOM_LAYOUT_ID, name: state.layoutName || 'Untitled', owner: 'Jordan Lee' }] : [];
  return [...ALL_LAYOUTS, ...custom];
}

function layoutNameFor(state: DemoState, id: string) {
  return layoutChoices(state).find((choice) => choice.id === id)?.name ?? '';
}

function resolveSkin(state: Pick<DemoState, 'skinId' | 'customSkin'>): TearsheetTheme {
  if (state.skinId === CUSTOM_SKIN_ID && state.customSkin) return state.customSkin;
  return TEARSHEET_THEMES[state.skinId as TearsheetThemeId] ?? TEARSHEET_THEMES.institutional;
}

function statusPillColors(status: DemoStatus, dark: boolean, info: { light: string; dark: string }) {
  switch (status) {
    case 'missing':
    case 'approved':
      return { bgcolor: hfAlpha(hf.caution, dark ? 0.4 : 0.22), color: dark ? hf.cautionBright : hf.cautionDeep };
    case 'submitted':
      return { bgcolor: hfAlpha(info.dark, dark ? 0.5 : 0.22), color: dark ? info.light : info.dark };
    case 'published':
      return { bgcolor: hfAlpha(dark ? hf.gainDark : hf.gain, dark ? 0.32 : 0.18), color: dark ? hf.gainDark : hf.gain };
    default:
      return { bgcolor: dark ? hfAlpha(hf.white, 0.14) : hf.bgWash, color: dark ? hf.gray300 : hf.navy700 };
  }
}

const STATUS_LABEL: Record<DemoStatus, string> = {
  missing: 'Missing',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  published: 'Published',
};

function StatusPill({ status }: { status: DemoStatus }) {
  return (
    <HFChip
      size="small"
      label={STATUS_LABEL[status]}
      variant="filled"
      sx={(t) => ({
        height: 22,
        width: '6rem',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.02em',
        borderRadius: 1,
        ...statusPillColors(status, t.palette.mode === 'dark', t.palette.info),
        '& .MuiChip-label': { px: 0.75, py: 0, width: '100%', textAlign: 'center' },
        animation: 'hfDemoPop 0.4s ease',
      })}
    />
  );
}

function Caret() {
  return (
    <HFBox
      component="span"
      sx={{
        display: 'inline-block',
        width: '1px',
        height: 14,
        ml: '1px',
        verticalAlign: 'middle',
        bgcolor: 'text.primary',
        animation: 'hfDemoCaret 0.9s steps(1) infinite',
      }}
    />
  );
}

/** Outlined select / text field look-alike. `select` shows the chevron; `caret` shows a blinking insertion point. */
function FakeField({
  label,
  value,
  width,
  demo,
  open,
  select = false,
  caret = false,
  placeholder,
  height = 36,
}: {
  label?: string;
  value: ReactNode;
  width: number | string;
  demo?: string;
  open?: boolean;
  select?: boolean;
  caret?: boolean;
  placeholder?: string;
  height?: number;
}) {
  const empty = value === '' || value == null;
  return (
    <HFBox
      data-demo={demo}
      sx={{
        position: 'relative',
        width,
        flex: '0 0 auto',
        minWidth: 0,
        height,
        px: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        border: 1,
        borderColor: open || caret ? 'primary.main' : 'divider',
        boxShadow: open || caret ? (t) => `0 0 0 1px ${t.palette.primary.main}` : 'none',
        borderRadius: 1,
        fontSize: 13,
        bgcolor: 'inherit',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      {label ? (
        <HFBox
          component="span"
          sx={{
            position: 'absolute',
            top: 0,
            left: 8,
            px: 0.5,
            transform: 'translateY(-50%)',
            fontSize: 11,
            lineHeight: 1,
            color: open || caret ? 'primary.main' : 'text.secondary',
            bgcolor: 'inherit',
          }}
        >
          {label}
        </HFBox>
      ) : null}
      <HFBox
        component="span"
        sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: empty ? 'text.disabled' : 'inherit' }}
      >
        {empty && !caret ? placeholder : value}
        {caret ? <Caret /> : null}
      </HFBox>
      {select ? (
        <ExpandMoreIcon
          sx={{ fontSize: 18, color: 'text.secondary', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}
        />
      ) : null}
    </HFBox>
  );
}

function PopMenu({
  items,
  minWidth = 220,
  align = 'left',
}: {
  items: Array<{ demo: string; label: ReactNode; selected?: boolean }>;
  minWidth?: number;
  align?: 'left' | 'right';
}) {
  return (
    <HFBox
      sx={{
        position: 'absolute',
        top: 'calc(100% + 4px)',
        [align]: 0,
        zIndex: 8,
        minWidth,
        py: 0.5,
        borderRadius: 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: 8,
        transformOrigin: `top ${align}`,
        animation: 'hfDemoMenu 0.2s ease both',
      }}
    >
      {items.map((item) => (
        <HFBox
          key={item.demo}
          data-demo={item.demo}
          sx={(t) => ({
            px: 2,
            py: 0.9,
            fontSize: 13,
            whiteSpace: 'nowrap',
            bgcolor: item.selected ? hfAlpha(t.palette.primary.main, 0.12) : 'transparent',
          })}
        >
          {item.label}
        </HFBox>
      ))}
    </HFBox>
  );
}

function DemoModal({
  title,
  width,
  children,
  actions,
  noDividers = false,
}: {
  title: ReactNode;
  width: number;
  children: ReactNode;
  actions: ReactNode;
  noDividers?: boolean;
}) {
  return (
    <HFBox
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        display: 'grid',
        placeItems: 'center',
        bgcolor: hfAlpha(hf.navy900, 0.45),
        animation: 'hfDemoFade 0.25s ease both',
      }}
    >
      <HFBox
        sx={{
          width,
          maxHeight: SHELL_HEIGHT - 48,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 12,
          animation: 'hfDemoMenu 0.3s ease both',
          overflow: 'hidden',
        }}
      >
        <HFBox sx={{ px: 3, pt: 2, pb: 1.5, fontSize: 18, fontWeight: 700, borderBottom: noDividers ? 0 : 1, borderColor: 'divider' }}>{title}</HFBox>
        <HFBox sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, overflow: 'hidden' }}>{children}</HFBox>
        <HFBox
          sx={{ px: 3, pb: 2, pt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1.5, borderTop: noDividers ? 0 : 1, borderColor: 'divider' }}
        >
          {actions}
        </HFBox>
      </HFBox>
    </HFBox>
  );
}

function Rail() {
  return (
    <HFBox
      sx={{
        width: 72,
        flex: '0 0 72px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        py: 2,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: FADE_COLORS,
      }}
    >
      <HFBox sx={{ mb: 2 }}>
        <HFLogo variant="globe" height={30} />
      </HFBox>
      {[HomeIcon, TearsheetsIcon, InvestorPortalIcon].map((Icon, index) => (
        <HFBox
          key={index}
          sx={(t) => ({
            width: 44,
            height: 44,
            borderRadius: 1.5,
            display: 'grid',
            placeItems: 'center',
            color: index === 1 ? 'primary.main' : 'text.secondary',
            bgcolor: index === 1 ? hfAlpha(t.palette.primary.main, 0.12) : 'transparent',
          })}
        >
          <Icon sx={{ fontSize: 24 }} />
        </HFBox>
      ))}
      <HFBox sx={{ mt: 'auto', color: 'text.secondary' }}>
        <SettingsIcon />
      </HFBox>
    </HFBox>
  );
}

function TopBar({ view }: { view: DemoView }) {
  return (
    <HFBox
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 3,
        height: 56,
        flex: '0 0 56px',
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: FADE_COLORS,
      }}
    >
      <HFBox component="nav" sx={{ display: 'flex', gap: 0.25, height: '100%' }}>
        {TABS.map((tab) => {
          const selected = tab.view === view;
          return (
            <HFBox
              key={tab.view}
              data-demo={`tab-${tab.view}`}
              sx={{
                px: 1.75,
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: selected ? 'primary.main' : 'text.primary',
                borderBottom: '2px solid',
                borderColor: selected ? 'primary.main' : 'transparent',
                transition: 'color 0.2s ease, border-color 0.2s ease',
              }}
            >
              {tab.label}
            </HFBox>
          );
        })}
      </HFBox>
    </HFBox>
  );
}

/* ─── Reports ──────────────────────────────────────────────────────────────── */

function primaryAction(status: DemoStatus) {
  if (status === 'missing') return 'Create';
  if (status === 'draft') return 'Submit';
  if (status === 'submitted') return 'Approve';
  if (status === 'approved') return 'Publish';
  return 'Copy link';
}

function missingWash(t: { palette: { mode: string; warning: { main: string } } }) {
  return t.palette.mode === 'dark' ? hfAlpha(hf.caution, 0.4) : hfAlpha(t.palette.warning.main, 0.12);
}

function needsAction(status: DemoStatus) {
  return status !== 'published';
}

function ReportsView({ state }: { state: DemoState }) {
  const rows = state.statusFilter === 'missing' ? DEMO_BOARD.filter((row) => row.status === 'missing') : DEMO_BOARD;
  const groups = DEMO_FUNDS.map((fund) => ({ ...fund, rows: rows.filter((row) => row.fundId === fund.fundId) })).filter(
    (group) => group.rows.length > 0,
  );
  const missing = rows.filter((row) => row.status === 'missing').length;
  const pending = rows.filter((row) => needsAction(row.status)).length;
  const statusLabel = DEMO_STATUS_FILTERS.find((option) => option.value === state.statusFilter)?.label ?? 'All';
  return (
    <HFBox sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <HFBox sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <HFBox sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            {state.loading ? 'Loading reports…' : `${rows.length} ${rows.length === 1 ? 'report' : 'reports'} · ${DEMO_FUNDS.length} funds selected`}
          </HFBox>
          <HFBox sx={{ mt: 0.5, fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
            {`${DEMO_RANGE.from}–${DEMO_RANGE.to}${state.loading ? '' : ` · ${missing} missing · ${pending} need action`}`}
          </HFBox>
          <HFButton size="small" variant="text" sx={{ px: 0, minWidth: 0, fontSize: 12 }}>
            Collapse all
          </HFButton>
        </HFBox>
        <HFControlGroup label="Filters" sx={{ flexWrap: 'nowrap', gap: 1.5, pt: 2 }}>
          <FakeField label="Fund" value="All" width={200} select />
          <HFBox sx={{ position: 'relative', bgcolor: 'inherit' }}>
            <FakeField demo="filter-status" label="Status" value={statusLabel} width={150} select open={state.statusMenuOpen} />
            {state.statusMenuOpen ? (
              <PopMenu
                minWidth={150}
                items={DEMO_STATUS_FILTERS.map((option) => ({
                  demo: `status-option-${option.value}`,
                  label: option.label,
                  selected: option.value === state.statusFilter,
                }))}
              />
            ) : null}
          </HFBox>
          <FakeField label="From" value={DEMO_RANGE.from} width={130} select />
          <FakeField label="To" value={DEMO_RANGE.to} width={130} select />
        </HFControlGroup>
      </HFBox>

      {state.loading ? (
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[0, 1].map((index) => (
            <HFBox
              key={index}
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <HFSkeleton variant="rounded" height={22} width="40%" />
              {[0, 1, 2].map((row) => (
                <HFSkeleton key={row} variant="rounded" height={36} />
              ))}
            </HFBox>
          ))}
        </HFBox>
      ) : (
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groups.map((group, groupIndex) => (
            <HFBox
              key={`${state.statusFilter}-${group.fundId}`}
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                transition: FADE_COLORS,
                animation: 'hfDemoIn 0.4s ease both',
                animationDelay: `${groupIndex * 80}ms`,
              }}
            >
              <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ExpandMoreIcon sx={{ fontSize: 22 }} />
                <HFBox sx={{ fontWeight: 800, fontSize: 14, flex: 1 }}>{group.fundName}</HFBox>
                <HFBox sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {`${group.rows.length} reports · ${group.rows.filter((row) => row.status === 'missing').length} missing · ${group.rows.filter((row) => needsAction(row.status)).length} need action`}
                </HFBox>
              </HFBox>
              <HFBox
                sx={{
                  display: 'grid',
                  gridTemplateColumns: BOARD_COLUMNS,
                  columnGap: 1.5,
                  alignItems: 'center',
                  px: 1.25,
                  height: 36,
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'text.secondary',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <span>As of</span>
                <span>Layout</span>
                <HFBox component="span" sx={{ textAlign: 'center' }}>
                  Status
                </HFBox>
                <span>Created by</span>
                <HFBox component="span" sx={{ textAlign: 'right' }}>
                  Actions
                </HFBox>
              </HFBox>
              {group.rows.map((row) => {
                const highlighted = state.highlightRow === row.id;
                const missingRow = row.status === 'missing';
                return (
                  <HFBox
                    key={row.id}
                    sx={(t) => ({
                      display: 'grid',
                      gridTemplateColumns: BOARD_COLUMNS,
                      columnGap: 1.5,
                      alignItems: 'center',
                      px: 1.25,
                      height: 44,
                      fontSize: 13,
                      bgcolor: missingRow ? missingWash(t) : 'transparent',
                      boxShadow: highlighted ? `inset 3px 0 0 ${t.palette.primary.main}` : 'none',
                      transition: 'box-shadow 0.3s ease',
                    })}
                  >
                    <span>{row.asOf}</span>
                    <HFBox component="span" sx={{ color: 'text.secondary' }}>
                      {row.layout}
                    </HFBox>
                    <HFBox sx={{ display: 'flex', justifyContent: 'center' }}>
                      <StatusPill status={row.status} />
                    </HFBox>
                    <HFBox component="span" sx={{ color: 'text.secondary' }}>
                      {row.owner}
                    </HFBox>
                    <HFBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.5 }}>
                      <HFButton size="small" variant="outlined" disabled={missingRow} sx={ACTION_BTN_SX}>
                        View
                      </HFButton>
                      <HFButton size="small" variant="outlined" disabled={missingRow} sx={ACTION_BTN_SX}>
                        PDF
                      </HFButton>
                      <HFButton
                        size="small"
                        variant="outlined"
                        data-demo={row.id === DEMO_MISSING_ROW_ID ? 'row-create-missing' : undefined}
                        sx={ACTION_BTN_SX}
                      >
                        {primaryAction(row.status)}
                      </HFButton>
                      <HFButton
                        size="small"
                        variant="outlined"
                        disabled={row.status !== 'submitted' && row.status !== 'approved' && row.status !== 'published'}
                        sx={ACTION_BTN_SX}
                      >
                        {row.status === 'published' ? 'Unpublish' : 'Reject'}
                      </HFButton>
                    </HFBox>
                  </HFBox>
                );
              })}
            </HFBox>
          ))}
        </HFBox>
      )}
    </HFBox>
  );
}

/* ─── Report canvas (shared by builder, create, previews) ──────────────────── */

type CanvasBuilderState = Pick<DemoState, 'selected' | 'hoverCard' | 'hoverCell' | 'dragging' | 'dropPreview' | 'flash' | 'sizeMenu'>;

type CanvasProps = {
  items: GridItem[];
  skin: TearsheetTheme;
  statRows: string[];
  advisor: string;
  logo: boolean;
  fundName: string;
  font?: string;
  bold?: boolean;
  builder?: CanvasBuilderState;
  flash?: StencilId | null;
};

function freeCells(items: GridItem[], rowCount: number) {
  const taken = new Set<string>();
  for (const item of items) {
    for (let r = item.row; r < item.row + item.rows; r += 1) {
      for (let c = item.col; c < item.col + item.cols; c += 1) taken.add(`${r}-${c}`);
    }
  }
  const cells: Array<{ row: number; col: number }> = [];
  for (let row = 1; row <= rowCount; row += 1) {
    for (let col = 1; col <= GRID_COLUMNS; col += 1) if (!taken.has(`${row}-${col}`)) cells.push({ row, col });
  }
  return cells;
}

function BrandMark({ size, initials = 'LR' }: { size: number; initials?: string }) {
  return (
    <HFBox
      sx={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        fontSize: size * 0.36,
        fontWeight: 800,
        color: hf.white,
        background: `linear-gradient(137deg, ${hf.navy500}, ${hf.cyan300})`,
        animation: 'hfDemoPop 0.5s ease',
      }}
    >
      {initials}
    </HFBox>
  );
}

function SectionTitle({ skin, children }: { skin: TearsheetTheme; children: ReactNode }) {
  return (
    <HFBox
      sx={{
        mb: 0.75,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: skin.accent,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'color 0.5s ease',
      }}
    >
      {children}
    </HFBox>
  );
}

function VamiChart({ skin, height }: { skin: TearsheetTheme; height: number }) {
  const min = Math.min(...DEMO_VAMI);
  const max = Math.max(...DEMO_VAMI);
  const points = DEMO_VAMI.map((value, index) => {
    const x = (index / (DEMO_VAMI.length - 1)) * 300;
    const y = 100 - ((value - min) / (max - min || 1)) * 88;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg viewBox="0 0 300 110" width="100%" height={height} preserveAspectRatio="none" aria-hidden>
      <polygon points={`0,110 ${points.join(' ')} 300,110`} fill={hfAlpha(skin.accent, 0.16)} style={{ animation: 'hfDemoFade 1.2s ease both' }} />
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={skin.accent}
        strokeWidth={2.5}
        strokeLinejoin="round"
        pathLength={1}
        strokeDasharray={1}
        style={{ animation: 'hfDemoDraw 1.4s ease-out both' }}
      />
    </svg>
  );
}

function formatPct(value: number) {
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
}

function StencilBody({
  item,
  skin,
  statRows,
  advisor,
  logo,
  fundName,
}: {
  item: GridItem;
  skin: TearsheetTheme;
  statRows: string[];
  advisor: string;
  logo: boolean;
  fundName: string;
}) {
  const dark = isDarkTearsheetSkin(skin);
  const positive = dark ? hf.gainDark : hf.gain;
  const negative = dark ? hf.lossDark : hf.loss;
  const height = item.rows * GRID_ROW_PX + (item.rows - 1) * GRID_GAP_PX - 20;
  switch (item.id) {
    case 'header':
      return (
        <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%' }}>
          {logo ? <BrandMark size={30} /> : null}
          <HFBox sx={{ minWidth: 0 }}>
            <HFBox sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.15 }}>{fundName}</HFBox>
            <HFBox
              sx={{ fontSize: 11, color: skin.muted, whiteSpace: 'nowrap' }}
            >{`${advisor ? `${advisor} · ` : ''}${DEMO_SUBTITLE} · ${DEMO_CREATE_AS_OF}`}</HFBox>
          </HFBox>
          <HFBox sx={{ ml: 'auto', fontSize: 10, color: skin.muted, whiteSpace: 'nowrap' }}>Powered by HedgeFacts</HFBox>
        </HFBox>
      );
    case 'kpis':
      return (
        <HFBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1.5, height: '100%', alignItems: 'center' }}>
          {DEMO_KPIS.map((kpi) => (
            <HFBox key={kpi.label}>
              <HFBox sx={{ fontSize: 10, color: skin.muted, lineHeight: 1.2 }}>{kpi.label}</HFBox>
              <HFBox sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2, color: kpi.value.startsWith('+') ? positive : skin.text }}>
                {kpi.value}
              </HFBox>
            </HFBox>
          ))}
        </HFBox>
      );
    case 'vami':
      return (
        <>
          <SectionTitle skin={skin}>{STENCIL_LABELS.vami}</SectionTitle>
          <VamiChart skin={skin} height={Math.max(40, height - 22)} />
        </>
      );
    case 'monthly':
      return (
        <>
          <SectionTitle skin={skin}>{STENCIL_LABELS.monthly}</SectionTitle>
          <HFBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 0.5, textAlign: 'center' }}>
            {DEMO_MONTHLY.map((month) => (
              <HFBox key={month.label} sx={{ py: 0.5, borderRadius: 0.5, bgcolor: skin.panel2, transition: FADE_COLORS }}>
                <HFBox sx={{ fontSize: 10, color: skin.muted }}>{month.label}</HFBox>
                <HFBox sx={{ fontSize: 11, fontWeight: 700, color: month.value < 0 ? negative : positive }}>{formatPct(month.value)}</HFBox>
              </HFBox>
            ))}
          </HFBox>
        </>
      );
    case 'risk':
      return (
        <>
          <SectionTitle skin={skin}>{STENCIL_LABELS.risk}</SectionTitle>
          {statRows.map((label) => (
            <HFBox
              key={label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.35,
                fontSize: 11,
                borderBottom: `1px solid ${skin.border}`,
                animation: 'hfDemoIn 0.35s ease both',
              }}
            >
              <HFBox component="span" sx={{ color: skin.muted }}>
                {label}
              </HFBox>
              <HFBox component="span" sx={{ fontWeight: 700, color: DEMO_STATS[label]?.startsWith('-') ? negative : skin.text }}>
                {DEMO_STATS[label]}
              </HFBox>
            </HFBox>
          ))}
        </>
      );
    case 'drawdowns':
      return (
        <>
          <SectionTitle skin={skin}>{STENCIL_LABELS.drawdowns}</SectionTitle>
          <HFBox
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 11,
              '& td, & th': { py: 0.4, textAlign: 'left', borderBottom: `1px solid ${skin.border}` },
            }}
          >
            <thead>
              <HFBox component="tr" sx={{ color: skin.muted }}>
                <th>Start</th>
                {item.cols > 1 ? <th>Length</th> : null}
                <HFBox component="th" sx={{ textAlign: 'right !important' }}>
                  Depth
                </HFBox>
              </HFBox>
            </thead>
            <tbody>
              {DEMO_DRAWDOWNS.map((row) => (
                <tr key={row.start}>
                  <td>{row.start}</td>
                  {item.cols > 1 ? <td>{row.length}</td> : null}
                  <HFBox component="td" sx={{ textAlign: 'right !important', fontWeight: 700, color: negative }}>
                    {formatPct(row.depth)}
                  </HFBox>
                </tr>
              ))}
            </tbody>
          </HFBox>
        </>
      );
    case 'commentary':
      return (
        <>
          <SectionTitle skin={skin}>{STENCIL_LABELS.commentary}</SectionTitle>
          <HFBox sx={{ fontSize: 12, lineHeight: 1.55 }}>{DEMO_COMMENTARY}</HFBox>
        </>
      );
    case 'disclaimer':
      return (
        <HFBox sx={{ fontSize: 10, color: skin.muted, display: 'flex', alignItems: 'center', height: '100%' }}>
          For illustration only — seed data. Past performance is not indicative of future results.
        </HFBox>
      );
  }
}

function CardPill({ demo, skin, children, active }: { demo?: string; skin: TearsheetTheme; children: ReactNode; active?: boolean }) {
  return (
    <HFBox
      data-demo={demo}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: 1,
        minHeight: 24,
        borderRadius: 999,
        border: `1px solid ${active ? skin.accent : hfAlpha(skin.border, 0.9)}`,
        bgcolor: skin.panel2,
        color: skin.text,
        boxShadow: `0 1px 3px ${hfAlpha(hf.navy900, 0.12)}`,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        '& svg': { color: skin.muted },
      }}
    >
      {children}
    </HFBox>
  );
}

function CardControls({ item, skin, sizeMenuOpen }: { item: GridItem; skin: TearsheetTheme; sizeMenuOpen: boolean }) {
  const width = DEMO_WIDTHS.find((option) => option.cols === item.cols)?.label ?? 'Full';
  return (
    <HFBox
      sx={{
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.4,
        animation: 'hfDemoFade 0.2s ease both',
      }}
    >
      <HFBox sx={{ position: 'relative' }}>
        <CardPill demo={`size-${item.id}`} skin={skin} active={sizeMenuOpen}>
          Size {width}
          <ExpandMoreIcon sx={{ fontSize: 14 }} />
        </CardPill>
        {sizeMenuOpen ? (
          <PopMenu
            minWidth={96}
            align="right"
            items={DEMO_WIDTHS.map((option) => ({ demo: `size-option-${option.cols}`, label: option.label, selected: option.cols === item.cols }))}
          />
        ) : null}
      </HFBox>
      {item.id === 'risk' || item.id === 'kpis' || item.id === 'commentary' ? (
        <CardPill demo={`edit-${item.id}`} skin={skin}>
          <EditIcon sx={{ fontSize: 12 }} />
          Edit
        </CardPill>
      ) : null}
      <CardPill skin={skin}>
        <ContentCopyIcon sx={{ fontSize: 12 }} />
        Duplicate
      </CardPill>
      <CardPill skin={skin}>
        <DeleteIcon sx={{ fontSize: 12 }} />
        Delete
      </CardPill>
    </HFBox>
  );
}

/** Tearsheet grid painted with skin tokens; in builder mode shows cell guides, selection, and the drag veil like `ReportCanvas`. */
export function DemoCanvas({ items, skin, statRows, advisor, logo, fundName, font = 'arial', bold = false, builder, flash }: CanvasProps) {
  const rowCount = gridRowCount(items);
  const fontStack = DEMO_FONTS.find((option) => option.value === font)?.stack;
  const pulse = builder?.flash ?? flash;
  return (
    <HFBox
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: skin.bg,
        color: skin.text,
        border: `1px solid ${skin.border}`,
        fontFamily: fontStack,
        '& *': { fontWeight: bold ? 600 : undefined },
        transition: FADE_COLORS,
      }}
    >
      <HFBox
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rowCount}, ${GRID_ROW_PX}px)`,
          gap: `${GRID_GAP_PX}px`,
        }}
      >
        {builder
          ? freeCells(items, rowCount).map((cell) => {
              const hovered = builder.hoverCell === `${cell.row}-${cell.col}`;
              return (
                <HFBox
                  key={`guide-${cell.row}-${cell.col}`}
                  data-demo={`cell-${cell.row}-${cell.col}`}
                  sx={{
                    gridColumn: cell.col,
                    gridRow: cell.row,
                    borderRadius: 1,
                    border: `1px ${hovered ? 'dashed' : 'solid'} ${hfAlpha(skin.accent, hovered ? 0.8 : 0.42)}`,
                    bgcolor: hfAlpha(skin.accent, hovered ? 0.14 : 0.06),
                    opacity: hovered || builder.dragging ? 1 : 0.55,
                    display: 'grid',
                    placeItems: 'center',
                    color: skin.accent,
                    transition: 'opacity 0.15s ease, background-color 0.15s ease',
                  }}
                >
                  {hovered ? <AddIcon sx={{ fontSize: 18, animation: 'hfDemoPop 0.3s ease' }} /> : null}
                </HFBox>
              );
            })
          : null}
        {items.map((item) => {
          const selected = builder?.selected === item.id;
          const veil = builder && (builder.hoverCard === item.id || builder.dragging === item.id);
          return (
            <HFBox
              key={item.id}
              data-demo={`stencil-${item.id}`}
              sx={{
                gridColumn: `${item.col} / span ${item.cols}`,
                gridRow: `${item.row} / span ${item.rows}`,
                position: 'relative',
                minWidth: 0,
                zIndex: selected || veil ? 2 : 1,
                borderRadius: 1,
                bgcolor: skin.panel,
                border: `1px solid ${selected || veil ? skin.accent : skin.border}`,
                boxShadow: selected ? `0 0 0 2px ${hfAlpha(skin.accent, 0.3)}` : 'none',
                opacity: builder?.dragging === item.id ? 0.4 : 1,
                transition: `${FADE_COLORS}, opacity 0.2s ease, box-shadow 0.2s ease`,
                animation: pulse === item.id ? 'hfDemoPop 0.6s ease' : undefined,
              }}
            >
              <HFBox sx={{ height: '100%', overflow: 'hidden', px: 1.5, py: 1.25 }}>
                <StencilBody item={item} skin={skin} statRows={statRows} advisor={advisor} logo={logo} fundName={fundName} />
              </HFBox>
              {veil ? (
                <HFBox
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 1,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: hfAlpha(skin.panel2, 0.55),
                    backdropFilter: 'blur(2px)',
                    animation: 'hfDemoFade 0.15s ease both',
                  }}
                >
                  <HFBox
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 999,
                      border: `1px solid ${skin.border}`,
                      bgcolor: hfAlpha(skin.panel, 0.88),
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Hold to drag
                  </HFBox>
                </HFBox>
              ) : null}
              {selected ? <CardControls item={item} skin={skin} sizeMenuOpen={builder?.sizeMenu === item.id} /> : null}
            </HFBox>
          );
        })}
        {builder?.dropPreview ? (
          <HFBox
            sx={{
              gridColumn: `${builder.dropPreview.col} / span ${builder.dropPreview.cols}`,
              gridRow: `${builder.dropPreview.row} / span ${builder.dropPreview.rows}`,
              zIndex: 3,
              borderRadius: 1,
              border: `2px dashed ${skin.accent}`,
              bgcolor: hfAlpha(skin.accent, 0.12),
              animation: 'hfDemoFade 0.2s ease both',
            }}
          />
        ) : null}
      </HFBox>
    </HFBox>
  );
}

function CanvasSkeleton() {
  return (
    <HFBox
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1, p: 1.5, borderRadius: 1.5, border: 1, borderColor: 'divider' }}
    >
      <HFSkeleton variant="rounded" height={48} sx={{ gridColumn: 'span 4' }} />
      <HFSkeleton variant="rounded" height={48} sx={{ gridColumn: 'span 4' }} />
      <HFSkeleton variant="rounded" height={160} sx={{ gridColumn: 'span 2' }} />
      <HFSkeleton variant="rounded" height={160} sx={{ gridColumn: 'span 2' }} />
      <HFSkeleton variant="rounded" height={160} sx={{ gridColumn: 'span 2' }} />
    </HFBox>
  );
}

/* ─── Layout Builder ───────────────────────────────────────────────────────── */

function SkinTile({ demo, name, swatch, selected }: { demo: string; name: string; swatch: string; selected: boolean }) {
  return (
    <HFBox
      data-demo={demo}
      sx={{
        width: 76,
        height: 44,
        p: 0.5,
        borderRadius: 1,
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        boxShadow: selected ? (t) => `0 0 0 1px ${t.palette.primary.main}` : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        transition: 'border-color 0.2s ease',
        animation: demo === `skin-${CUSTOM_SKIN_ID}` ? 'hfDemoPop 0.5s ease' : undefined,
      }}
    >
      <HFBox sx={{ flex: 1, borderRadius: 0.5, background: swatch }} />
      <HFBox
        sx={{
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1.2,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {name}
      </HFBox>
    </HFBox>
  );
}

function skinSwatch(colors: TearsheetTheme) {
  return `linear-gradient(135deg, ${colors.bg}, ${colors.accent})`;
}

function toolbarSkins(state: DemoState): Array<{ id: string; name: string; swatch: string }> {
  const stock = DEMO_SKIN_ORDER.map((id) => {
    const chip = SKINS.find((item) => item.skinId === id);
    return { id, name: chip?.name ?? id, swatch: chip?.swatch ?? '' };
  });
  if (!state.customSkin) return stock;
  return [...stock.slice(0, 4), { id: CUSTOM_SKIN_ID, name: state.customSkin.name, swatch: skinSwatch(state.customSkin) }];
}

function BuilderView({ state }: { state: DemoState }) {
  const skin = resolveSkin(state);
  return (
    <HFBox sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HFBox sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
        <HFControlGroup label="Report context" sx={{ flexWrap: 'nowrap', gap: 2.5 }}>
          <FakeField label="Fund" value={DEMO_FUNDS[0].fundName} width={230} select />
          <FakeField label="As of" value={DEMO_CREATE_AS_OF} width={140} select />
        </HFControlGroup>
        <HFControlGroup label="Skin" sx={{ ml: 'auto', flexWrap: 'nowrap', gap: 0.5 }}>
          {toolbarSkins(state).map((item) => (
            <SkinTile key={item.id} demo={`skin-${item.id}`} name={item.name} swatch={item.swatch} selected={state.skinId === item.id} />
          ))}
          <HFBox
            data-demo="skin-edit-menu"
            sx={{
              width: 76,
              height: 44,
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            <EditIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            Edit menu
          </HFBox>
        </HFControlGroup>
      </HFBox>
      <HFControlGroup label="Layout" sx={{ width: '100%', flexWrap: 'nowrap', gap: 1 }}>
        <HFBox sx={{ position: 'relative', bgcolor: 'inherit' }}>
          <FakeField demo="layout-select" value={state.layoutName || 'Untitled'} width={180} select open={state.layoutMenuOpen} />
          {state.layoutMenuOpen ? (
            <PopMenu
              minWidth={300}
              items={layoutChoices(state).map((choice) => ({
                demo: `layout-option-${choice.id}`,
                selected: choice.id === state.layoutId,
                label: (
                  <HFBox sx={{ display: 'grid', gridTemplateColumns: '14ch auto', columnGap: 1 }}>
                    <HFBox component="span" sx={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {choice.name}
                    </HFBox>
                    <HFBox component="span" sx={{ color: 'text.secondary' }}>
                      ({choice.owner})
                    </HFBox>
                  </HFBox>
                ),
              }))}
            />
          ) : null}
        </HFBox>
        <FakeField demo="layout-name" label="Name" value={state.layoutName} width={180} caret={state.typing === 'layout-name'} />
        <HFButton data-demo="copy-as" size="small" variant={state.customLayout ? 'outlined' : 'contained'} sx={CHROME_BTN_SX}>
          Copy as
        </HFButton>
        <HFButton size="small" variant="outlined" disabled={!state.customLayout} sx={CHROME_BTN_SX}>
          Delete
        </HFButton>
        <HFButton data-demo="edit-favorites" size="small" variant="text" sx={{ ...CHROME_BTN_SX, px: 1.5 }}>
          Edit favorites
        </HFButton>
        <HFBox sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <HFButton size="small" startIcon={<AddIcon sx={CHROME_ICON_SX} />} sx={CHROME_BTN_SX}>
            Add stencil
          </HFButton>
          <HFButton size="small" variant="outlined" startIcon={<UndoIcon sx={CHROME_ICON_SX} />} disabled={!state.dirty} sx={CHROME_BTN_SX}>
            Undo
          </HFButton>
          <HFButton size="small" variant="outlined" startIcon={<RedoIcon sx={CHROME_ICON_SX} />} disabled sx={CHROME_BTN_SX}>
            Redo
          </HFButton>
          <HFButton
            data-demo="save-layout"
            size="small"
            variant={(state.dirty && state.customLayout) || state.saving === 'layout' ? 'contained' : 'outlined'}
            loading={state.saving === 'layout'}
            disabled={!state.customLayout || (!state.dirty && state.saving !== 'layout')}
            startIcon={<SaveIcon sx={CHROME_ICON_SX} />}
            sx={CHROME_BTN_SX}
          >
            Save
          </HFButton>
        </HFBox>
      </HFControlGroup>
      {state.loading ? (
        <CanvasSkeleton />
      ) : (
        <DemoCanvas
          items={state.items}
          skin={skin}
          statRows={state.statRows}
          advisor={state.advisor}
          logo={state.logo === 'done'}
          fundName={DEMO_FUNDS[0].fundName}
          font={state.font}
          bold={state.bold}
          builder={state}
        />
      )}
    </HFBox>
  );
}

function AddStencilModal({ state }: { state: DemoState }) {
  const modal = state.addModal!;
  const query = modal.query.trim().toLowerCase();
  const filtered = query ? DEMO_ADDABLE.filter((section) => section.label.toLowerCase().includes(query)) : DEMO_ADDABLE;
  const inLayout = new Set<string>(state.items.map((item) => item.id));
  const picked = DEMO_ADDABLE.find((section) => section.value === modal.picked);
  const pickedKnown = Object.keys(STENCIL_LABELS).includes(modal.picked);
  return (
    <DemoModal
      title="Add stencil"
      width={880}
      actions={
        <>
          <HFButton variant="outlined">Cancel</HFButton>
          <HFButton data-demo="add-confirm">Add to layout</HFButton>
        </>
      }
    >
      <HFBox sx={{ display: 'grid', gridTemplateColumns: '280px minmax(0, 1fr)', gap: 2, height: 400 }}>
        <HFBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 1.25,
            bgcolor: 'background.default',
            minHeight: 0,
          }}
        >
          <HFBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <HFBox sx={{ fontSize: 13, fontWeight: 700 }}>Stencils</HFBox>
            <HFChip size="small" label={`${filtered.length}`} />
          </HFBox>
          <HFBox sx={{ bgcolor: 'background.default', pt: 1 }}>
            <FakeField
              demo="add-filter"
              label="Filter stencils"
              value={modal.query}
              placeholder="Search by name…"
              width="100%"
              caret={state.typing === 'add-filter'}
            />
          </HFBox>
          <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'hidden', minHeight: 0 }}>
            {filtered.slice(0, 7).map((section) => {
              const active = section.value === modal.picked;
              return (
                <HFButton
                  key={section.value}
                  data-demo={`add-item-${section.value}`}
                  fullWidth
                  variant={active ? 'contained' : 'outlined'}
                  sx={{
                    justifyContent: 'flex-start',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    height: 48,
                    minHeight: 48,
                    px: 1.25,
                    borderColor: active ? 'primary.main' : 'divider',
                    flex: '0 0 auto',
                  }}
                >
                  <HFBox component="span" sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.25 }}>
                    {section.label}
                  </HFBox>
                  <HFBox component="span" sx={{ fontSize: 11, opacity: inLayout.has(section.value) ? 0.72 : 0, fontWeight: 400 }}>
                    {inLayout.has(section.value) ? 'Already in layout — adds a copy' : '\u00a0'}
                  </HFBox>
                </HFButton>
              );
            })}
          </HFBox>
        </HFBox>
        <HFBox
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <HFBox sx={{ px: 1.5, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
            <HFBox sx={{ fontSize: 14, fontWeight: 700 }}>{picked?.label ?? 'Preview'}</HFBox>
            <HFBox sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>Sample preview with illustrative data</HFBox>
          </HFBox>
          <HFBox key={modal.picked} sx={{ position: 'relative', p: 1.25, flex: 1, animation: 'hfDemoFade 0.3s ease both' }}>
            {pickedKnown ? (
              <DemoCanvas
                items={[{ id: modal.picked as StencilId, col: 1, row: 1, cols: 4, rows: 4 }]}
                skin={resolveSkin(state)}
                statRows={state.statRows}
                advisor="Sample Advisor"
                logo={false}
                fundName="Sample Program"
              />
            ) : (
              <HFSkeleton variant="rounded" height={220} animation={false} />
            )}
            <HFBox
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                pointerEvents: 'none',
                fontSize: 28,
                fontWeight: 800,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'text.disabled',
                opacity: 0.45,
                transform: 'rotate(-12deg)',
              }}
            >
              Sample data
            </HFBox>
          </HFBox>
        </HFBox>
      </HFBox>
    </DemoModal>
  );
}

function StatsEditModal({ state }: { state: DemoState }) {
  const checked = state.editModal!.rows;
  const rows = [...checked, ...DEMO_STAT_OPTIONS.filter((label) => !checked.includes(label))];
  return (
    <DemoModal
      title={STENCIL_LABELS.risk}
      width={560}
      actions={
        <>
          <HFButton variant="outlined">Cancel</HFButton>
          <HFButton data-demo="edit-apply">Apply</HFButton>
        </>
      }
    >
      <HFBox sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>Appearance</HFBox>
      <HFBox sx={{ display: 'flex', gap: 2, pt: 0.5, bgcolor: 'background.paper' }}>
        <FakeField label="Font" value="Use global" width={160} select />
        <FakeField label="Size" value="Use global" width={140} select />
        <FakeField label="Weight" value="Use global" width={140} select />
      </HFBox>
      <HFBox sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>Rows</HFBox>
      <HFBox sx={{ fontSize: 13, color: 'text.secondary' }}>{`Drag to reorder. ${checked.length} selected.`}</HFBox>
      <HFBox sx={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map((label) => (
          <HFBox
            key={label}
            data-demo={`stat-row-${label}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, height: 34, borderRadius: 1 }}
          >
            <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <HFCheckbox label={label} checked={checked.includes(label)} size="small" />
          </HFBox>
        ))}
      </HFBox>
    </DemoModal>
  );
}

function SkinColorCell({ token, value, typing }: { token: (typeof SKIN_TOKENS)[number]; value: string; typing: string | null }) {
  return (
    <HFBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <HFBox sx={{ fontSize: 12, fontWeight: 600, textAlign: 'center' }}>{SKIN_TOKEN_LABELS[token]}</HFBox>
      <HFBox
        sx={{
          width: 28,
          height: 28,
          borderRadius: 0.75,
          border: 1,
          borderColor: 'divider',
          bgcolor: value,
          transition: 'background-color 0.3s ease',
        }}
      />
      <HFBox sx={{ width: '100%', bgcolor: 'background.paper' }}>
        <FakeField demo={`skin-hex-${token}`} value={typing ?? value} width="100%" height={30} caret={typing != null} />
      </HFBox>
    </HFBox>
  );
}

function SkinMenuModal({ state }: { state: DemoState }) {
  const panel = state.skinModal!;
  const draft = state.skinDraft;
  const pinned = toolbarSkins(state);
  return (
    <DemoModal
      title="Skins"
      width={860}
      actions={
        <>
          <HFButton variant="outlined">Cancel</HFButton>
          <HFButton data-demo="skin-save-close" loading={state.saving === 'skin'}>
            Save and close
          </HFButton>
        </>
      }
    >
      <HFBox sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
        {(
          [
            ['setup', 'Setup'],
            ['create', 'Create new'],
          ] as const
        ).map(([value, label]) => (
          <HFBox
            key={value}
            data-demo={`skin-tab-${value}`}
            sx={{
              px: 2,
              py: 1,
              fontSize: 14,
              fontWeight: 600,
              color: panel === value ? 'primary.main' : 'text.secondary',
              borderBottom: '2px solid',
              borderColor: panel === value ? 'primary.main' : 'transparent',
            }}
          >
            {label}
          </HFBox>
        ))}
      </HFBox>
      {panel === 'setup' ? (
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <HFBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <HFBox sx={SECTION_SX}>On the toolbar</HFBox>
            <HFChip size="small" label={`${pinned.length} / 5`} />
          </HFBox>
          <HFBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1 }}>
            {pinned.map((item) => (
              <HFBox
                key={item.id}
                sx={{ height: 72, p: 0.75, borderRadius: 1, border: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}
              >
                <HFBox sx={{ flex: 1, borderRadius: 0.5, background: item.swatch }} />
                <HFBox sx={{ fontSize: 12, fontWeight: 600 }}>{item.name}</HFBox>
              </HFBox>
            ))}
          </HFBox>
          <HFBox sx={SECTION_SX}>Public skins</HFBox>
          <HFBox sx={{ color: 'text.secondary', fontSize: 12 }}>None</HFBox>
          <HFBox sx={SECTION_SX}>My skins</HFBox>
          <HFBox sx={{ color: 'text.secondary', fontSize: 12 }}>None</HFBox>
        </HFBox>
      ) : (
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2, animation: 'hfDemoFade 0.25s ease both' }}>
          <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 1, bgcolor: 'background.paper' }}>
            <FakeField demo="skin-name" label="Name" value={draft.name} width={280} caret={state.typing === 'skin-name'} />
            <HFButton>Save</HFButton>
          </HFBox>
          <HFBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1 }}>
            {SKIN_TOKENS.map((token) => (
              <SkinColorCell key={token} token={token} value={draft[token]} typing={state.hexTyping?.token === token ? state.hexTyping.text : null} />
            ))}
          </HFBox>
          <HFBox sx={{ height: 190, overflow: 'hidden', borderRadius: 1.5 }}>
            <HFBox sx={{ width: '125%', transform: 'scale(0.8)', transformOrigin: 'top left' }}>
              <DemoCanvas
                items={[
                  { id: 'header', col: 1, row: 1, cols: 4, rows: 1 },
                  { id: 'kpis', col: 1, row: 2, cols: 4, rows: 1 },
                  { id: 'vami', col: 1, row: 3, cols: 2, rows: 3 },
                  { id: 'risk', col: 3, row: 3, cols: 2, rows: 3 },
                ]}
                skin={draft}
                statRows={state.statRows}
                advisor="Sample Advisor"
                logo={false}
                fundName="Sample Program"
              />
            </HFBox>
          </HFBox>
        </HFBox>
      )}
    </DemoModal>
  );
}

function FavoritesModal({ state }: { state: DemoState }) {
  const slots = state.favoritesModal!;
  const pinned = new Set(slots.filter(Boolean));
  const choices = layoutChoices(state);
  const stock = choices.filter((choice) => DEMO_STOCK_LAYOUTS.some((layout) => layout.id === choice.id) && !pinned.has(choice.id));
  const custom = choices.filter((choice) => !DEMO_STOCK_LAYOUTS.some((layout) => layout.id === choice.id) && !pinned.has(choice.id));
  const full = pinned.size >= DEMO_FAVORITE_SLOTS;
  const card = (choice: { id: string; name: string; owner: string }) => (
    <HFBox
      key={choice.id}
      data-demo={`fav-card-${choice.id}`}
      sx={{
        px: 1.25,
        py: 1,
        minHeight: 52,
        minWidth: 0,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        opacity: full ? 0.5 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      <HFBox sx={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{choice.name}</HFBox>
      <HFBox sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{choice.owner}</HFBox>
    </HFBox>
  );
  return (
    <DemoModal
      title="Edit favorite layouts"
      width={760}
      actions={
        <>
          <HFButton variant="outlined">Cancel</HFButton>
          <HFButton data-demo="fav-save-close" loading={state.saving === 'favorites'}>
            Save and close
          </HFButton>
        </>
      }
    >
      <HFBox
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <HFBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <HFBox sx={SECTION_SX}>Preview on Create</HFBox>
          <HFChip size="small" label={`${pinned.size} / ${DEMO_FAVORITE_SLOTS}`} />
        </HFBox>
        <HFBox sx={{ display: 'grid', gridTemplateColumns: `repeat(${DEMO_FAVORITE_SLOTS}, minmax(0, 1fr))`, gap: 1 }}>
          {slots.map((id, index) =>
            id ? (
              <HFBox
                key={`${index}-${id}`}
                sx={{
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  border: 1,
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  minWidth: 0,
                  overflow: 'hidden',
                  animation: 'hfDemoPop 0.4s ease',
                }}
              >
                <HFBox
                  sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {layoutNameFor(state, id)}
                </HFBox>
                <HFBox data-demo={`fav-clear-${index}`} sx={{ display: 'grid', placeItems: 'center', width: 24, height: 24, color: 'primary.main' }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </HFBox>
              </HFBox>
            ) : (
              <HFBox
                key={`${index}-empty`}
                sx={{
                  height: 36,
                  display: 'grid',
                  placeItems: 'center',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontSize: 12,
                  color: 'text.secondary',
                }}
              >
                Slot {index + 1}
              </HFBox>
            ),
          )}
        </HFBox>
        <HFBox sx={{ fontSize: 12, color: 'text.secondary' }}>
          These quick picks appear in the Favorite layouts block on Create. Remove a slot, then pick a layout below.
        </HFBox>
      </HFBox>
      <HFBox sx={{ pt: 1, bgcolor: 'background.paper' }}>
        <FakeField label="Search layouts" value="" placeholder="Filter by name or owner" width="100%" />
      </HFBox>
      {stock.length ? (
        <>
          <HFBox sx={SECTION_SX}>Stock layouts</HFBox>
          <HFBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>{stock.map(card)}</HFBox>
        </>
      ) : null}
      {custom.length ? (
        <>
          <HFBox sx={SECTION_SX}>Custom layouts</HFBox>
          <HFBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>{custom.map(card)}</HFBox>
        </>
      ) : null}
    </DemoModal>
  );
}

/* ─── Branding ─────────────────────────────────────────────────────────────── */

function Wysiwyg({ demo, label, value, caret, rows }: { demo?: string; label: string; value: string; caret: boolean; rows: number }) {
  return (
    <HFBox
      data-demo={demo}
      sx={{
        position: 'relative',
        border: 1,
        borderColor: caret ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: 'inherit',
        minWidth: 0,
        transition: 'border-color 0.2s ease',
      }}
    >
      <HFBox
        component="span"
        sx={{
          position: 'absolute',
          top: 0,
          left: 8,
          px: 0.5,
          transform: 'translateY(-50%)',
          fontSize: 11,
          lineHeight: 1,
          color: 'text.secondary',
          bgcolor: 'inherit',
        }}
      >
        {label}
      </HFBox>
      <HFBox
        sx={{
          display: 'flex',
          gap: 1.5,
          px: 1.5,
          pt: 1.25,
          pb: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
          fontSize: 13,
          color: 'text.secondary',
        }}
      >
        <b>B</b>
        <i>I</i>
        <u>U</u>
        <span>•</span>
        <span>1.</span>
        <span>🔗</span>
      </HFBox>
      <HFBox sx={{ px: 1.5, py: 1, fontSize: 13, lineHeight: 1.5, height: rows * 20 + 16, overflow: 'hidden' }}>
        {value}
        {caret ? <Caret /> : null}
      </HFBox>
    </HFBox>
  );
}

function UploadCard({ title, state, demo, square = false }: { title: string; state: DemoState['logo']; demo: string; square?: boolean }) {
  const size = 112;
  return (
    <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: square ? size : '100%', minWidth: 0 }}>
      <HFBox sx={{ fontSize: 14, fontWeight: 700 }}>{title}</HFBox>
      <HFBox
        sx={{
          position: 'relative',
          width: square ? size : '100%',
          height: size,
          display: 'grid',
          placeItems: 'center',
          border: state === 'uploading' ? 0 : '1px dashed',
          borderColor: 'divider',
          borderRadius: square ? '50%' : 1,
          overflow: 'hidden',
          bgcolor: square ? 'action.selected' : 'action.hover',
        }}
      >
        {state === 'uploading' ? (
          <HFSkeleton
            variant={square ? 'circular' : 'rounded'}
            animation="wave"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transform: 'none' }}
          />
        ) : state === 'done' ? (
          square ? (
            <BrandMark size={size} initials="JL" />
          ) : (
            <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 1, animation: 'hfDemoPop 0.5s ease' }}>
              <BrandMark size={44} />
              <HFBox sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.1 }}>
                Larkspur
                <br />
                Ridge
              </HFBox>
            </HFBox>
          )
        ) : square ? (
          <AccountCircleIcon sx={{ fontSize: 96, color: 'action.disabled' }} />
        ) : (
          <HFBox sx={{ color: 'text.secondary', fontSize: 13 }}>No logo</HFBox>
        )}
      </HFBox>
      {state === 'uploading' ? (
        <HFSkeleton variant="rounded" height={30} />
      ) : (
        <HFButton data-demo={demo} size="small" fullWidth>
          Upload
        </HFButton>
      )}
      {state === 'done' ? (
        <HFButton size="small" variant="outlined" fullWidth>
          Remove
        </HFButton>
      ) : null}
    </HFBox>
  );
}

function SaveRow({ demo, saving }: { demo: string; saving: boolean }) {
  return (
    <HFBox sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
      <HFButton data-demo={demo} loading={saving}>
        Save
      </HFButton>
    </HFBox>
  );
}

function BrandingView({ state }: { state: DemoState }) {
  if (state.loading) {
    return (
      <HFBox sx={{ p: 3, maxWidth: 960, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <HFSkeleton variant="rounded" height={72} />
        <HFSkeleton variant="rounded" height={300} />
        <HFSkeleton variant="rounded" height={120} />
      </HFBox>
    );
  }
  const fontLabel = DEMO_FONTS.find((option) => option.value === state.font)?.label ?? 'Arial';
  return (
    <HFBox sx={{ p: 3, pt: 4, width: '100%', maxWidth: 960, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3, boxSizing: 'border-box' }}>
      <HFControlGroup label="Brand identity" sx={BRAND_GROUP_SX}>
        <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', bgcolor: 'inherit' }}>
          <HFBox sx={{ position: 'relative', bgcolor: 'inherit' }}>
            <FakeField demo="font-select" label="Font" value={fontLabel} width={180} select open={state.fontMenuOpen} />
            {state.fontMenuOpen ? (
              <PopMenu
                minWidth={180}
                items={DEMO_FONTS.map((option) => ({
                  demo: `font-option-${option.value}`,
                  label: option.label,
                  selected: option.value === state.font,
                }))}
              />
            ) : null}
          </HFBox>
          <FakeField label="Font size" value="Medium" width={150} select />
          <HFBox data-demo="bold-switch">
            <HFSwitch label="Bold" checked={state.bold} />
          </HFBox>
          <HFButton data-demo="fonts-save" sx={{ ml: 'auto' }} loading={state.saving === 'fonts'}>
            Save
          </HFButton>
        </HFBox>
      </HFControlGroup>

      <HFControlGroup label="Header and disclaimer" sx={BRAND_GROUP_SX}>
        <HFBox sx={{ display: 'flex', gap: 2, width: '100%', bgcolor: 'inherit' }}>
          <HFBox sx={{ flex: 1, bgcolor: 'inherit' }}>
            <FakeField label="Subtitle" value={DEMO_SUBTITLE} width="100%" />
          </HFBox>
          <FakeField demo="advisor-field" label="Advisor name" value={state.advisor} width={260} caret={state.typing === 'advisor-field'} />
        </HFBox>
        <HFBox
          sx={{ display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr)', columnGap: 3, alignItems: 'start', width: '100%', bgcolor: 'inherit' }}
        >
          <UploadCard title="Company logo" state={state.logo} demo="logo-upload" />
          <HFBox sx={{ pt: 3.5, bgcolor: 'inherit' }}>
            <Wysiwyg demo="disclaimer-field" label="Disclaimer" value={state.disclaimer} caret={state.typing === 'disclaimer-field'} rows={6} />
          </HFBox>
        </HFBox>
        <SaveRow demo="header-save" saving={state.saving === 'header'} />
      </HFControlGroup>

      <HFBox data-demo="group-benchmarks" sx={{ bgcolor: 'transparent' }}>
        <HFControlGroup label="Benchmarks" sx={BRAND_GROUP_SX}>
          <HFBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {state.benchmarks.map((name) => (
              <HFChip
                key={name}
                label={name}
                onDelete={() => undefined}
                deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                sx={{ animation: 'hfDemoPop 0.4s ease' }}
              />
            ))}
          </HFBox>
          <HFBox sx={{ position: 'relative', bgcolor: 'inherit' }}>
            <FakeField
              demo="bench-search"
              label="Search indexes"
              value={state.benchQuery}
              placeholder="Type at least 2 characters"
              width="100%"
              caret={state.typing === 'bench-search'}
            />
            {state.benchQuery.length >= 2 ? (
              <HFBox
                sx={{
                  border: 1,
                  borderTop: 0,
                  borderColor: 'divider',
                  borderRadius: '0 0 4px 4px',
                  bgcolor: 'background.paper',
                  animation: 'hfDemoFade 0.2s ease both',
                }}
              >
                {DEMO_INDEXES.map((name, index) => (
                  <HFBox key={name} data-demo={`bench-option-${index}`} sx={{ px: 1.25, py: 1, fontSize: 13, fontWeight: 600 }}>
                    {name}
                  </HFBox>
                ))}
              </HFBox>
            ) : null}
          </HFBox>
          <SaveRow demo="bench-save" saving={state.saving === 'benchmarks'} />
        </HFControlGroup>
      </HFBox>

      <HFBox data-demo="group-pm">
        <HFControlGroup label="Portfolio manager" sx={BRAND_GROUP_SX}>
          <HFBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '112px minmax(0, 1fr)',
              columnGap: 3,
              alignItems: 'start',
              width: '100%',
              bgcolor: 'inherit',
            }}
          >
            <UploadCard title="Manager photo" state={state.pmPhoto} demo="pm-photo-upload" square />
            <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 3.5, bgcolor: 'inherit', minWidth: 0 }}>
              <HFBox sx={{ display: 'flex', gap: 2, bgcolor: 'inherit' }}>
                <FakeField
                  demo="pm-name"
                  label="Name"
                  value={state.pmName}
                  placeholder="e.g. Alex Rivera"
                  width={220}
                  caret={state.typing === 'pm-name'}
                />
                <FakeField label="Role" value="Portfolio Manager" width={220} />
                <FakeField label="Contact info" value="jordan@larkspur-ridge.example" width={240} />
              </HFBox>
              <Wysiwyg label="Description" value={DEMO_PM_BIO} caret={false} rows={3} />
            </HFBox>
          </HFBox>
          <SaveRow demo="pm-save" saving={state.saving === 'manager'} />
        </HFControlGroup>
      </HFBox>
    </HFBox>
  );
}

/* ─── Create / workflow / PDF ──────────────────────────────────────────────── */

function CreateView({ state }: { state: DemoState }) {
  const moreLayouts = layoutChoices(state).filter((choice) => !state.favorites.includes(choice.id));
  return (
    <HFBox sx={{ p: 3, pb: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {state.status ? (
        <HFBox>
          <HFButton size="small" variant="text" sx={{ ...CHROME_BTN_SX, px: 0.5, minWidth: 0 }}>
            Back to reports
          </HFButton>
        </HFBox>
      ) : null}
      <HFBox sx={{ display: 'grid', gridTemplateColumns: '520px minmax(0, 1fr)', gap: 2 }}>
        <HFControlGroup label="Report context" sx={{ flexWrap: 'nowrap', gap: 2.5, height: 56 }}>
          <HFBox sx={{ position: 'relative', bgcolor: 'inherit' }}>
            <FakeField
              demo="create-fund"
              label="Fund"
              value={state.fundPicked ? DEMO_CREATE_FUND.fundName : ''}
              width={240}
              select
              open={state.fundMenuOpen}
            />
            {state.fundMenuOpen ? <PopMenu items={DEMO_FUNDS.map((fund) => ({ demo: `fund-option-${fund.fundId}`, label: fund.fundName }))} /> : null}
          </HFBox>
          <FakeField label="As of" value={state.fundPicked ? DEMO_CREATE_AS_OF : ''} width={180} select />
        </HFControlGroup>
        <HFBox sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
          <HFControlGroup label="Favorite layouts" sx={{ flex: 1, flexWrap: 'nowrap', height: 56, minWidth: 0 }}>
            {state.favorites.filter(Boolean).map((id) => (
              <HFButton
                key={id}
                data-demo={`fav-layout-${id}`}
                size="small"
                variant={state.layoutId === id ? 'contained' : 'outlined'}
                sx={{ ...CHROME_BTN_SX, flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}
              >
                <HFBox
                  component="span"
                  sx={{
                    display: 'block',
                    width: '100%',
                    minWidth: 0,
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {layoutNameFor(state, id)}
                </HFBox>
              </HFButton>
            ))}
          </HFControlGroup>
          <HFControlGroup label="Layout" sx={{ height: 56 }}>
            <FakeField
              value={moreLayouts.some((choice) => choice.id === state.layoutId) ? state.layoutName : ''}
              placeholder="More layouts"
              width={150}
              select
            />
          </HFControlGroup>
        </HFBox>
      </HFBox>
      {!state.fundPicked ? (
        <HFBox
          sx={{
            height: 360,
            borderRadius: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
            display: 'grid',
            placeItems: 'center',
            fontSize: 14,
            color: 'text.secondary',
          }}
        >
          Pick a fund and month to compose the tearsheet
        </HFBox>
      ) : state.loading ? (
        <CanvasSkeleton />
      ) : (
        <DemoCanvas
          items={state.items}
          skin={resolveSkin(state)}
          statRows={state.statRows}
          advisor={state.advisor}
          logo={state.logo === 'done'}
          fundName={DEMO_CREATE_FUND.fundName}
          font={state.font}
          bold={state.bold}
        />
      )}
    </HFBox>
  );
}

function WorkflowBar({ state }: { state: DemoState }) {
  const btn = (demo: string, label: string, primary = false, disabled = false) => (
    <HFButton
      key={demo}
      data-demo={demo}
      size="small"
      variant={primary ? 'contained' : 'outlined'}
      loading={primary && state.busy}
      disabled={disabled}
      sx={CHROME_BTN_SX}
    >
      {label}
    </HFButton>
  );
  const actions =
    state.status === null
      ? [btn('wf-save', 'Save draft', true, !state.fundPicked || state.loading), btn('wf-submit-disabled', 'Submit', false, true)]
      : state.status === 'draft'
        ? [btn('wf-save-again', 'Save draft'), btn('wf-submit', 'Submit', true)]
        : state.status === 'submitted'
          ? [btn('wf-approve', 'Approve', true), btn('wf-reject', 'Reject')]
          : state.status === 'approved'
            ? [btn('wf-publish', 'Publish', true), btn('wf-reject', 'Reject')]
            : [btn('wf-copy', 'Copy link'), btn('wf-unpublish', 'Unpublish')];
  return (
    <HFBox
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 24,
        mx: 'auto',
        width: 'fit-content',
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 1,
        borderRadius: 999,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: 6,
        animation: 'hfDemoIn 0.35s ease both',
      }}
    >
      {state.status ? <StatusPill key={state.status} status={state.status} /> : null}
      {actions}
      {btn('wf-export', 'Export to PDF', true, state.status === null)}
    </HFBox>
  );
}

function Toast({ message }: { message: string }) {
  return (
    <HFBox
      key={message}
      role="status"
      sx={(t) => ({
        position: 'absolute',
        right: 24,
        bottom: 24,
        zIndex: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.25,
        borderRadius: 1.5,
        fontSize: 13,
        fontWeight: 600,
        color: hf.white,
        bgcolor: t.palette.mode === 'dark' ? hf.slate600 : hf.navy700,
        boxShadow: 6,
        animation: 'hfDemoToast 0.35s ease both',
      })}
    >
      <HFBox sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: hf.gainDark }} />
      {message}
    </HFBox>
  );
}

function PdfOption({ demo, label, selected }: { demo: string; label: string; selected: boolean }) {
  return (
    <HFBox data-demo={demo} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, fontSize: 14 }}>
      <HFBox
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: 2,
          borderColor: selected ? 'primary.main' : 'text.secondary',
          display: 'grid',
          placeItems: 'center',
          transition: 'border-color 0.2s ease',
        }}
      >
        {selected ? <HFBox sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', animation: 'hfDemoPop 0.3s ease' }} /> : null}
      </HFBox>
      {label}
    </HFBox>
  );
}

function PdfPage({ state, items, delay }: { state: DemoState; items: GridItem[]; delay: number }) {
  return (
    <HFBox
      sx={{
        width: 132,
        height: 172,
        overflow: 'hidden',
        borderRadius: 0.5,
        border: 1,
        borderColor: 'divider',
        boxShadow: 4,
        animation: 'hfDemoPage 0.5s ease both',
        animationDelay: `${delay}ms`,
      }}
    >
      <HFBox sx={{ width: 600, transform: 'scale(0.22)', transformOrigin: 'top left' }}>
        <DemoCanvas
          items={items}
          skin={resolveSkin(state)}
          statRows={state.statRows}
          advisor={state.advisor}
          logo={state.logo === 'done'}
          fundName={DEMO_CREATE_FUND.fundName}
          font={state.font}
        />
      </HFBox>
    </HFBox>
  );
}

function PdfDialog({ state }: { state: DemoState }) {
  const rendering = state.pdf === 'rendering';
  const firstPage = state.items.filter((item) => item.row <= 8);
  const secondPage = state.items.filter((item) => item.row > 8).map((item) => ({ ...item, row: item.row - 8 }));
  if (state.pdf === 'done') {
    return (
      <DemoModal
        title="PDF ready"
        width={440}
        noDividers
        actions={
          <>
            <HFButton variant="outlined">Close</HFButton>
            <HFButton data-demo="pdf-open">Open PDF</HFButton>
          </>
        }
      >
        <HFBox sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <PdfPage state={state} items={firstPage} delay={0} />
          <PdfPage state={state} items={secondPage} delay={180} />
        </HFBox>
        <HFBox sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center' }}>
          {`Tearsheet — ${DEMO_CREATE_FUND.fundName} — ${DEMO_CREATE_AS_OF}.pdf · 2 pages · ${state.pdfMode === 'a4' ? 'A4' : 'Letter'}`}
        </HFBox>
      </DemoModal>
    );
  }
  return (
    <DemoModal
      title="Export PDF"
      width={440}
      noDividers
      actions={
        <>
          <HFButton variant="outlined" disabled={rendering}>
            Cancel
          </HFButton>
          <HFButton data-demo="pdf-generate" loading={rendering}>
            Generate PDF
          </HFButton>
        </>
      }
    >
      <HFBox>
        <HFBox sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>Format</HFBox>
        <PdfOption demo="pdf-continuous" label="Single continuous page (Letter)" selected={state.pdfMode === 'continuous'} />
        <PdfOption demo="pdf-a4" label="Paginated A4 (stencils stay whole)" selected={state.pdfMode === 'a4'} />
      </HFBox>
      {rendering ? (
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <HFBox sx={{ height: 6, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <HFBox sx={{ height: '100%', width: `${state.pdfProgress}%`, bgcolor: 'primary.main', transition: 'width 0.2s linear' }} />
          </HFBox>
          <HFBox sx={{ fontSize: 12, color: 'text.secondary' }}>{`Rendering page ${state.pdfProgress < 50 ? 1 : 2} of 2…`}</HFBox>
        </HFBox>
      ) : null}
    </DemoModal>
  );
}

export function DemoShell({ state, scrollRef }: { state: DemoState; scrollRef: Ref<HTMLDivElement> }) {
  const view =
    state.view === 'reports' ? (
      <ReportsView state={state} />
    ) : state.view === 'builder' ? (
      <BuilderView state={state} />
    ) : state.view === 'branding' ? (
      <BrandingView state={state} />
    ) : (
      <CreateView state={state} />
    );
  return (
    <HFBox
      sx={(t) => ({
        position: 'relative',
        width: SHELL_WIDTH,
        height: SHELL_HEIGHT,
        display: 'flex',
        overflow: 'hidden',
        fontFamily: t.typography.fontFamily,
        bgcolor: 'background.default',
        color: 'text.primary',
        transition: FADE_COLORS,
      })}
    >
      <Rail />
      <HFBox sx={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar view={state.view} />
        <HFBox ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <HFBox key={state.view} sx={{ animation: 'hfDemoIn 0.35s ease both' }}>
            {view}
          </HFBox>
        </HFBox>
        {state.view === 'create' && state.fundPicked ? <WorkflowBar state={state} /> : null}
      </HFBox>
      {state.addModal ? <AddStencilModal state={state} /> : null}
      {state.editModal ? <StatsEditModal state={state} /> : null}
      {state.skinModal ? <SkinMenuModal state={state} /> : null}
      {state.favoritesModal ? <FavoritesModal state={state} /> : null}
      {state.pdf !== 'closed' ? <PdfDialog state={state} /> : null}
      {state.toast ? <Toast message={state.toast} /> : null}
    </HFBox>
  );
}
